import { useQuery } from '@tanstack/react-query';
import * as Dfns from 'date-fns/fp';
import { AdminLambdaFetch, awaitJSON, LinearLambdaFetch } from '@src/utils/fetch-utils';
import { DATE_FORMAT, MediaTypeDimension, PrimaryDimension } from './linear-pacing-constants';

interface LinearPacingParams {
  start: string;
  end: string;
  grouping: PrimaryDimension;
  mediaType: MediaTypeDimension;
}

// Weekly pacing summary, grouped by company or network, for the Client Detail tab.
const fetchLinearPacing = async ({
  start,
  end,
  grouping,
  mediaType
}: LinearPacingParams): Promise<LinearPacingRow[]> =>
  awaitJSON<LinearPacingRow[]>(
    await AdminLambdaFetch('/linear/pacing', {
      params: { start, end, grouping, secured: mediaType.toLowerCase() }
    })
  );

export interface LinearPacingSpend {
  spend: number;
  days?: Record<string, { spend: number } | undefined>;
}

// Spend and status fields shared by top-level rows and their flights.
export interface LinearPacingTotals {
  booked: LinearPacingSpend;
  estimated: LinearPacingSpend;
  estimatedAll: LinearPacingSpend;
  logs: LinearPacingSpend;
  logsAll: LinearPacingSpend;
  unsecuredLogs: LinearPacingSpend;
  unsecuredLogsAll: LinearPacingSpend;
  securedLogs: LinearPacingSpend;
  securedLogsAll: LinearPacingSpend;
  tvBudget?: number | null;
  commission: number | null;
  assumedClearanceRate?: number;
  hasBvs?: boolean;
  hasPrelogs?: boolean;
  lastPrelogDay?: string | null;
}

// One company x network flight (the nested rows under a top-level /linear/pacing row).
export interface LinearPacingFlight extends LinearPacingTotals {
  company?: string;
  network?: string;
}

// A top-level /linear/pacing row: the grouping key is a string and the other dimension holds the
// row's flights (e.g. grouping=company → { company: 'acme', network: [...flights] }).
export interface LinearPacingRow extends LinearPacingTotals {
  company: string | LinearPacingFlight[];
  network: string | LinearPacingFlight[];
}

export interface LinearPacingLogRow {
  avail: string;
  length: number;
  dow: string;
  daypart: string | null;
  rotation: string | null;
  type: string;
  count: number;
  cost: number;
  spend: number;
  // One entry per day of the week, Monday first.
  spots: { cost: number }[];
}

export const DETAIL_QUERY_KEY = 'linearPacingDetail';

// Client Detail's week: the end date is start + 7 days.
export const useLinearPacingDetail = ({
  start,
  grouping,
  mediaType
}: Omit<LinearPacingParams, 'end'>) =>
  useQuery({
    queryKey: [DETAIL_QUERY_KEY, start, grouping, mediaType],
    enabled: Boolean(start),
    // The page raises its own error dialog.
    meta: { suppressGlobalError: true },
    queryFn: () =>
      fetchLinearPacing({
        start,
        end: Dfns.format(DATE_FORMAT, Dfns.addDays(7, Dfns.parseISO(start))),
        grouping,
        mediaType
      })
  });

interface LinearPacingLogsParams {
  week: string;
  company?: string;
  network?: string;
  mediaType: MediaTypeDimension;
}

export const useLinearPacingLogs = ({
  week,
  company,
  network,
  mediaType
}: LinearPacingLogsParams) =>
  useQuery({
    queryKey: ['linearPacingLogs', week, company, network, mediaType],
    enabled: Boolean(company && network),
    meta: { suppressGlobalError: true },
    queryFn: async () =>
      awaitJSON<LinearPacingLogRow[]>(
        await LinearLambdaFetch('/pacing_logs', {
          params: { week, company, network, secured: mediaType.toLowerCase() }
        })
      )
  });
